import { Component, Inject, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslocoService } from '@ngneat/transloco';
import { addDays, format, parse, startOfToday, subDays } from 'date-fns';
import * as lodash from 'lodash-es';
import { DialogsService } from '../dialogs.service';
import { PlanningArea, ServiceUserAssignment, ServiceUserAssignmentAlias } from '../../modules/admin/services/services.types';
import { FuseConfirmationService } from '../../../@fuse/services/confirmation/confirmation.service';

@Component({
    selector: 'app-alias-dialog',
    templateUrl: './alias-dialog.component.html',
    styles: [
        /* language=SCSS */
        `
            .alias-dialog-panel {

                .mat-mdc-dialog-container {

                    .mat-mdc-dialog-surface {
                        padding: 0 !important;
                    }
                }
            }
        `
    ],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AliasDialogComponent {

    formArray: FormArray<FormGroup<{
        serviceId: FormControl<string>,
        planningAreaId: FormControl<string>,
        userId: FormControl<string>,
        userAlias: FormControl<string>,
        from: FormControl<string | Date>,
        to: FormControl<string | Date>
    }>> = new FormArray([]);
    resource: ServiceUserAssignment;

    constructor(
        @Inject(MAT_DIALOG_DATA) public data: {
            resource: ServiceUserAssignment,
            area: PlanningArea
        },
        public dialogRef: MatDialogRef<AliasDialogComponent>,
        private dialogsService: DialogsService,
        private _fuseConfirmationService: FuseConfirmationService,
        private transloco: TranslocoService
    ) {
        this.resource = lodash.cloneDeep(this.data.resource);
        let isFirst = true;
        for (const alias of this.resource.userAlias) {
            this.formArray.push(this.userAliasAsFormGroup(alias, isFirst));
            isFirst = false;
        }
    }

    async closeDialog() {
        this.dialogRef.close();
    }

    save() {
        const confirmDialog = this.openConfirmDialog(this.transloco.translate('services.resources.confirmSaveAlias'));
        confirmDialog.afterClosed().subscribe((confirm) => {
            if (confirm === 'confirmed') {
                this.formArray.markAllAsTouched();
                if (!this.formArray.valid) {
                    this.dialogsService.openErrorDialog({
                        message: this.transloco.translate('errorsInForm')
                    });
                    return;
                }
                const alias: ServiceUserAssignmentAlias[] = [];
                let index = 0;
                for (let item of this.formArray.getRawValue()) {
                    alias.push({
                        ...item,
                        from: typeof item.from === 'string' ? item.from : format(item.from, 'yyyy-MM-dd'),
                        to: Boolean(item.to) ? typeof item.to === 'string' ? item.to : format(item.to, 'yyyy-MM-dd') : undefined,
                        userAlias: item.userAlias.toUpperCase()
                    });
                    if (index < this.formArray.length - 1 && !alias[index].to) {
                        alias[index].to = format(this.previousDate(this.formArray.getRawValue()[index + 1].from), 'yyyy-MM-dd');
                    }
                    index++;
                }
                this.dialogRef.close(alias);
            }
        });
    }

    openDatepickerOnClick(datepicker: MatDatepicker<Date>) {
        if (!datepicker.opened) {
            datepicker.open();
        }
    }

    addAlias() {
        const newAlias: ServiceUserAssignmentAlias = {
            serviceId: this.resource.serviceId,
            planningAreaId: this.data.area.planningArea,
            userId: this.resource.userId,
            userAlias: '',
            from: '',
            to: '', 
        }
        this.formArray.push(this.userAliasAsFormGroup(newAlias));
    }

    deleteAlias(index: number) {
        const confirmDialog = this.openConfirmDialog(this.transloco.translate('services.resources.confirmDeleteAlias'));
        confirmDialog.afterClosed().subscribe((confirm) => {
            if (confirm === 'confirmed') {
                this.formArray.removeAt(index);
            }
        });
    }

    previousDate(date: string | Date) {
        if (!date) {
            return undefined;
        }
        const dateObject = typeof date === 'string' ? parse(date, 'yyyy-MM-dd', startOfToday()) : date;
        return subDays(dateObject, 1);
    }

    nextDate(date: string | Date) {
        if (!date) {
            return undefined;
        }
        const dateObject = typeof date === 'string' ? parse(date, 'yyyy-MM-dd', startOfToday()) : date;
        return addDays(dateObject, 1);
    }

    private userAliasAsFormGroup(userAlias: ServiceUserAssignmentAlias, isFirst = false) {
        return new FormGroup({
            serviceId: new FormControl<string>(userAlias.serviceId),
            planningAreaId: new FormControl<string>(userAlias.planningAreaId),
            userId: new FormControl<string>(userAlias.userId),
            userAlias: new FormControl<string>(userAlias.userAlias),
            from: new FormControl<string | Date>({ value: userAlias.from, disabled: isFirst }),
            to: new FormControl<string | Date>(userAlias.to),
        })
    }

    private openConfirmDialog(message: string) {
        return this._fuseConfirmationService.open({
            title: this.transloco.translate('warning'),
            message: message,
            actions: {
                confirm: {
                    label: this.transloco.translate('confirm')
                },
                cancel: {
                    label: this.transloco.translate('cancel')
                }
            }
        });
    }
}
